import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";

export const SIGNUP_ROUTE = "/login?mode=signup";

/** Brief hold so the tap feedback is perceivable before the route swap. */
const NAVIGATE_DELAY_MS = 350;

type GetStartedButtonProps = Pick<ButtonProps, "variant" | "size" | "className"> & {
  to?: string;
  children?: ReactNode;
};

/**
 * Get Started CTA: shows immediate tap feedback (spinner + "Starting…"),
 * ignores duplicate taps while pending, then navigates to the signup route.
 */
const GetStartedButton = ({
  to = SIGNUP_ROUTE,
  variant = "default",
  size = "lg",
  className,
  children,
}: GetStartedButtonProps) => {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleClick = useCallback(() => {
    if (pending) return;
    setPending(true);
    timerRef.current = window.setTimeout(() => navigate(to), NAVIGATE_DELAY_MS);
  }, [pending, navigate, to]);

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      aria-busy={pending || undefined}
      aria-disabled={pending || undefined}
    >
      {pending ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Starting…
        </span>
      ) : (
        <>
          {children ?? "Get Started"}
          <ArrowRight className="h-4 w-4" />
        </>
      )}
    </Button>
  );
};

export default GetStartedButton;
